import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ArrowLeft, Flame, RotateCcw, SkipForward } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import type { Contact } from "../types";
import { SwipeCard } from "./SwipeCard";
import { SwipeFilterBar } from "./SwipeFilterBar";
import { actionWrites } from "./actions";
import { refinementsFor, type SwipeRefinementSelection } from "./refinements";
import { GESTURE_ORDER, type SwipeScenario, type SwipeTone } from "./types";
import { COMMIT_DISTANCE, useSwipeGesture } from "./useSwipeGesture";
import { useSwipeQueue } from "./useSwipeQueue";

/** How long the discarded card stays on screen flying away. */
const FLY_OUT_MS = 320;

const TONE_BUTTON: Record<SwipeTone, string> = {
  positive:
    "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300",
  neutral:
    "border-amber-500/40 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 dark:text-amber-300",
  negative:
    "border-rose-500/40 bg-rose-500/10 text-rose-700 hover:bg-rose-500/20 dark:text-rose-300",
  special:
    "border-violet-500/40 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 dark:text-violet-300",
};

const TONE_OVERLAY: Record<SwipeTone, string> = {
  positive: "border-emerald-500 text-emerald-600 dark:text-emerald-400",
  neutral: "border-amber-500 text-amber-600 dark:text-amber-400",
  negative: "border-rose-500 text-rose-600 dark:text-rose-400",
  special: "border-violet-500 text-violet-600 dark:text-violet-400",
};

const ARROW: Record<string, string> = {
  right: "→",
  left: "←",
  up: "↑",
  down: "↓",
};

/** A short buzz on a decision. Silently absent on desktop and on iOS Safari. */
const buzz = (ms: number) => {
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    try {
      navigator.vibrate(ms);
    } catch {
      // Vibration is a nicety; a browser refusing it must not break a swipe.
    }
  }
};

interface ExitingCard {
  contact: Contact;
  direction: "right" | "left" | "up" | "down";
  /** Distinguishes consecutive exits of the same contact after an undo. */
  nonce: number;
}

/**
 * The shared swipe interface.
 *
 * Every scenario runs through this: the same card, the same gestures, the same
 * undo, the same keyboard. It knows a scenario has a question and some actions
 * and nothing else about what any of them mean.
 */
export const SwipeRunner = ({ scenario }: { scenario: SwipeScenario }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Refinements live in the URL, which makes a scenario-plus-filter a link.
  // Read back through the scenario's own list so a stray query param cannot
  // widen or reshape the queue.
  const selection = useMemo<SwipeRefinementSelection>(() => {
    const next: SwipeRefinementSelection = {};
    for (const refinement of refinementsFor(scenario)) {
      const value = searchParams.get(refinement.id);
      if (value && refinement.choices.some((c) => c.value === value)) {
        next[refinement.id] = value;
      }
    }
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, searchParams.toString()]);

  // Pinned for the life of the mount: a `now` that moved every render would
  // rebuild every time-dependent filter and re-key the query every render.
  // The dependency is deliberate — switching scenarios should re-read the
  // clock, so "due before now" is fresh each time a scenario is opened.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [scenario.id]);

  const queue = useSwipeQueue(scenario, selection, now);
  const [exiting, setExiting] = useState<ExitingCard | null>(null);
  const exitCounter = useRef(0);

  const { current, answer, skip, undo, canUndo } = queue;

  const setSelection = useCallback(
    (next: SwipeRefinementSelection) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(next)) {
        if (value) params.set(key, value);
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  /**
   * Record one decision. `index` is a position in `scenario.actions`, or "skip".
   *
   * Everything funnels through here — gesture, button, and key — so the three
   * input paths cannot drift apart. Actions past the third have no gesture
   * slot, so they borrow the upward fly-out.
   */
  const dispatch = useCallback(
    (index: number | "skip") => {
      if (!current) return;
      const action = index === "skip" ? undefined : scenario.actions[index];
      if (index !== "skip" && !action) return;

      const direction =
        index === "skip" ? "down" : (GESTURE_ORDER[index] ?? "up");
      exitCounter.current += 1;
      setExiting({ contact: current, direction, nonce: exitCounter.current });
      buzz(action ? 14 : 8);
      if (action) answer(action);
      else skip();
    },
    [current, scenario.actions, answer, skip],
  );

  const commit = useCallback(
    (direction: "right" | "left" | "up" | "down") => {
      if (direction === "down") return dispatch("skip");
      const index = GESTURE_ORDER.indexOf(direction);
      if (index < 0 || index >= scenario.actions.length) return;
      dispatch(index);
    },
    [dispatch, scenario.actions.length],
  );

  const gesture = useSwipeGesture(commit, Boolean(current));

  const handleUndo = useCallback(() => {
    if (!canUndo) return;
    setExiting(null);
    buzz(10);
    undo();
  }, [canUndo, undo]);

  // Keyboard is the desktop half of the same interface: number keys pick any
  // action (including a fourth, which has no gesture), arrows mirror the
  // gestures, and undo is one key away so a misfire never costs a page visit.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("input,textarea,[contenteditable=true]")) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const numeric = Number(event.key);
      if (numeric >= 1 && numeric <= scenario.actions.length) {
        event.preventDefault();
        dispatch(numeric - 1);
        return;
      }

      switch (event.key) {
        case "ArrowRight":
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          commit(
            event.key === "ArrowRight"
              ? "right"
              : event.key === "ArrowLeft"
                ? "left"
                : "up",
          );
          break;
        case "ArrowDown":
        case "s":
          event.preventDefault();
          commit("down");
          break;
        case "u":
        case "Backspace":
          event.preventDefault();
          handleUndo();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [commit, dispatch, handleUndo, scenario.actions.length]);

  useEffect(() => {
    if (!exiting) return;
    const timer = window.setTimeout(() => setExiting(null), FLY_OUT_MS);
    return () => window.clearTimeout(timer);
  }, [exiting]);

  const intentAction =
    gesture.intent && gesture.intent !== "down"
      ? scenario.actions[GESTURE_ORDER.indexOf(gesture.intent)]
      : undefined;
  const dragProgress = Math.min(
    1,
    Math.hypot(gesture.dx, gesture.dy) / COMMIT_DISTANCE,
  );

  return (
    // Height-bounded on a phone rather than left to grow: MobileContent is
    // min-h-screen, and 100vh in mobile Safari is TALLER than the visible
    // viewport, so anything at the bottom of the page sits under the nav and
    // the browser toolbar with no way to scroll to it. 100dvh tracks the real
    // visible height; the subtraction is MobileContent's own pt-18 + pb-20.
    // The card below takes the slack, so the answers are always on screen.
    <div className="mx-auto flex h-[calc(100dvh-9.5rem)] w-full max-w-md flex-col gap-2 sm:h-auto sm:gap-3">
      <header className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild className="-ml-2 shrink-0">
          <Link to="/swipe" aria-label="Back to scenarios">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {/* The arrow says it on a phone; the word is part of what pushed
                the counters off the right edge of a 390px screen. */}
            <span className="hidden sm:inline">Scenarios</span>
          </Link>
        </Button>
        <SwipeFilterBar
          scenario={scenario}
          selection={selection}
          onChange={setSelection}
        />
        <div className="ml-auto flex shrink-0 items-center gap-2 text-sm whitespace-nowrap text-muted-foreground tabular-nums">
          {queue.streak >= 3 && (
            <span
              className="flex items-center gap-1 font-medium text-orange-500"
              title="Answered in a row"
            >
              <Flame className="h-4 w-4" aria-hidden />
              {queue.streak}
            </span>
          )}
          <span title="Answered this session">
            <strong className="text-foreground">{queue.answered}</strong> done
          </span>
          {queue.remaining !== undefined && (
            <span title="Still in this queue">{queue.remaining} left</span>
          )}
        </div>
      </header>

      <div>
        <h1 className="text-lg leading-tight font-semibold sm:text-xl">
          {scenario.question}
        </h1>
        {queue.isRanked && (
          <p className="mt-1 text-xs text-muted-foreground">
            Showing {queue.rankedRemaining} scored recommendation
            {queue.rankedRemaining === 1 ? "" : "s"} first, best match at the
            top. Everything else follows unranked.
          </p>
        )}
        {queue.isRecycling && (
          <p className="mt-1 text-xs text-muted-foreground">
            Everything has been reviewed once — now re-asking, oldest review
            first.
          </p>
        )}
      </div>

      {/*
        Viewport-relative on a phone so the actions always sit above the fold:
        a scenario whose hints wrap to two lines is 60px taller than one whose
        do not, and a fixed card height pushed the third answer under the nav
        bar. Still a fixed height within a session, so the buttons do not move
        between cards — the thing that would make fast swiping miss.
      */}
      <div className="relative min-h-[11rem] flex-1 select-none sm:h-[28rem] sm:flex-none">
        {queue.isPending && (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed">
            <Spinner />
          </div>
        )}

        {!queue.isPending && !current && (
          <EmptyState
            scenario={scenario}
            isRecycling={queue.isRecycling}
            isFiltered={Object.keys(selection).length > 0}
            onClearFilters={() => setSelection({})}
          />
        )}

        {queue.next && (
          <div
            aria-hidden
            className="absolute inset-0 translate-y-3 scale-[0.97] rounded-2xl border bg-card opacity-70 shadow-sm"
          />
        )}

        {current && (
          <div
            className="absolute inset-0 cursor-grab touch-none active:cursor-grabbing"
            style={gesture.style}
            onPointerDown={gesture.onPointerDown}
            onPointerMove={gesture.onPointerMove}
            onPointerUp={gesture.onPointerUp}
            onPointerCancel={gesture.onPointerUp}
          >
            <SwipeCard
              contact={current}
              scenario={scenario}
              suggestion={queue.suggestion}
            />
            {gesture.intent && (
              <div
                className={cn(
                  "pointer-events-none absolute inset-0 flex items-center justify-center rounded-2xl border-4",
                  intentAction
                    ? TONE_OVERLAY[intentAction.tone]
                    : "border-muted-foreground text-muted-foreground",
                )}
                style={{ opacity: 0.25 + dragProgress * 0.75 }}
              >
                <span className="rounded-lg bg-background/80 px-4 py-2 text-2xl font-bold tracking-tight uppercase">
                  {intentAction ? intentAction.label : "Skip"}
                </span>
              </div>
            )}
          </div>
        )}

        {exiting && (
          <FlyOutCard key={exiting.nonce} card={exiting} scenario={scenario} />
        )}
      </div>

      <div className="grid grid-cols-1 gap-1.5 sm:gap-2">
        {scenario.actions.map((action, index) => {
          const direction = GESTURE_ORDER[index];
          const isSuggested =
            current != null &&
            actionWrites(action, queue.suggestion?.suggested_value, current);
          return (
            <button
              key={action.id}
              type="button"
              disabled={!current}
              onClick={() => dispatch(index)}
              className={cn(
                "flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors disabled:opacity-40 sm:px-4 sm:py-2.5",
                TONE_BUTTON[action.tone],
                isSuggested && "ring-2 ring-primary/50 ring-offset-1",
              )}
            >
              <span className="w-6 shrink-0 text-center text-base font-bold opacity-70">
                {direction ? ARROW[direction] : index + 1}
              </span>
              <span className="min-w-0">
                <span className="block text-sm leading-tight font-semibold">
                  {action.label}
                </span>
                {action.hint && (
                  <span className="line-clamp-1 text-xs leading-tight opacity-75 sm:line-clamp-none">
                    {action.hint}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          disabled={!current}
          onClick={() => dispatch("skip")}
        >
          <SkipForward className="h-4 w-4" aria-hidden />
          Skip
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          disabled={!canUndo}
          onClick={handleUndo}
        >
          <RotateCcw className="h-4 w-4" aria-hidden />
          Undo
        </Button>
      </div>

      <p className="hidden text-center text-xs text-muted-foreground sm:block">
        Swipe the card, tap an answer, or use{" "}
        <kbd className="rounded border px-1">←</kbd>{" "}
        <kbd className="rounded border px-1">→</kbd>{" "}
        <kbd className="rounded border px-1">↑</kbd> ·{" "}
        <kbd className="rounded border px-1">S</kbd> skip ·{" "}
        <kbd className="rounded border px-1">U</kbd> undo
      </p>
    </div>
  );
};

/**
 * The discarded card, flying off in the direction it was thrown.
 *
 * Rendered outside the queue so the next card is already interactive
 * underneath — the animation is decoration, never a gate on the next decision.
 */
const FlyOutCard = ({
  card,
  scenario,
}: {
  card: ExitingCard;
  scenario: SwipeScenario;
}) => {
  const [away, setAway] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setAway(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const offset = {
    right: "translate3d(140%, -10%, 0) rotate(18deg)",
    left: "translate3d(-140%, -10%, 0) rotate(-18deg)",
    up: "translate3d(0, -140%, 0) rotate(4deg)",
    down: "translate3d(0, 60%, 0) rotate(-2deg)",
  }[card.direction];

  return (
    <div
      className="pointer-events-none absolute inset-0 z-10"
      style={{
        transform: away ? offset : "none",
        opacity: away ? 0 : 1,
        transition: `transform ${FLY_OUT_MS}ms ease-out, opacity ${FLY_OUT_MS}ms ease-out`,
      }}
    >
      <SwipeCard contact={card.contact} scenario={scenario} isBackdrop />
    </div>
  );
};

const EmptyState = ({
  scenario,
  isRecycling,
  isFiltered,
  onClearFilters,
}: {
  scenario: SwipeScenario;
  isRecycling: boolean;
  isFiltered: boolean;
  onClearFilters: () => void;
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed p-6 text-center">
    <p className="text-lg font-semibold">
      {isFiltered ? "Nobody matches" : "All clear"}
    </p>
    <p className="max-w-xs text-sm text-muted-foreground">
      {isFiltered
        ? `No contact matches “${scenario.title}” with these filters.`
        : isRecycling || !scenario.recycle
          ? `Nothing left to answer for “${scenario.title}”.`
          : `Every contact has an answer for “${scenario.title}”.`}
    </p>
    {isFiltered ? (
      <Button variant="outline" size="sm" onClick={onClearFilters}>
        Clear the filters
      </Button>
    ) : (
      <Button variant="outline" size="sm" asChild>
        <Link to="/swipe">Pick another scenario</Link>
      </Button>
    )}
  </div>
);
